import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { push } from '@lagunovsky/redux-react-router'
import i18n from 'simple-react-i18n'
import moment from 'moment/moment'
import SimpleTabSideList from '../../../components/navbar/SimpleTabSideList'
import PluviometryAction from '../../actions/PluviometryAction'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import { getStationArrowNav } from '../../../utils/ActionUtils'
import { getLinks, getStationTitleWithTownName, getStationTypeCodeFromType } from '../../../utils/StationUtils'
import { CIVIL_YEAR, PAST_YEARS } from '../../../alerting/constants/ChartFollowContants'
import GraphGenericPluvio from './graphs/GraphGenericPluvio'
import GraphEfficientRain from './graphs/GraphEfficientRain'
import NormalsGraphPluvio from './graphs/NormalsGraphPluvio'
import GraphMonthsStackRain from './graphs/GraphMonthsStackRain'
import GraphComparisonNormal from './graphs/GraphComparisonNormal'
import ChoiceTypeChart from './tabs/ChoiceTypeChart'
import PluvioSuiviPastYearsTab from './tabs/PluvioSuiviPastYearsTab'
import PluvioSuiviThresholdsTab from './tabs/PluvioSuiviThresholdsTab'
import PluvioSuiviModelsTab from './tabs/PluvioSuiviModelsTab'
import PluvioSuiviChartOptionsTab from './tabs/PluvioSuiviChartOptionsTab'
import PluvioSuiviAssociationsTab from './tabs/PluvioSuiviAssociationsTab'
import StationSuiviEventsTab from '../../../piezometry/components/suivi/components/chartTabs2/StationSuiviEventsTab'
import EventsAction from '../../../events/actions/EventsAction'
import StationAction from '../../../station/actions/StationAction'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import {
    CHARTOPTIONS_TAB,
    EFFICIENT_RAIN,
    EVENTS_TAB,
    GENERIC,
    LINKEDSTATIONS_TAB,
    MODELS_TAB,
    MONTHS_STACK,
    NORMAL_COMPARISON,
    NORMALS_MONTHS,
    NORMALS_YEARS,
    PLUVIO_FOLLOW_CHART_TYPE,
    STATS_TAB,
    THRESHOLDS_TAB,
} from './constants/PluvioChartTypes'
import { Card, CardContent, Grid } from '@mui/material'
import MessageCard from '../../../components/card/MessageCard'
import ProgressCard from '../../../components/card/ProgressCard'

const PluvioSuiviGraphApp = () => {
    const {
        pluviometer,
        pluviometerStatistics,
        pluviometers,
        pluviometryDataTypes,
        citiesIndex,
    } = useSelector(store => ({
        pluviometer: store.PluviometryReducer.pluviometer,
        pluviometerStatistics: store.PluviometryReducer.pluviometerStatistics,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const [typeChart, setTypeChart] = useLocalStorage(PLUVIO_FOLLOW_CHART_TYPE, GENERIC)
    const [stat, setStat] = useState()
    const [statsLoaded, setStatsLoaded] = useState(false)
    const [dateComparedNormal, setDateComparedNormal] = useState(moment().subtract(3, 'year').valueOf())
    const [showEnveloppe, setShowEnveloppe] = useState(false)
    const [time, setTime] = useState(CIVIL_YEAR)
    // Fenêtre initiale alignée sur l'onglet de temps par défaut (CIVIL_YEAR), pour que les
    // onglets latéraux (stations associées…) partent bornés dès le chargement, sans attendre
    // un premier clic sur les onglets. Resynchronisé ensuite via onChangeDates.
    const [minDate, setMinDate] = useState(moment().startOf('year').valueOf())
    const [maxDate, setMaxDate] = useState(moment().endOf('year').valueOf())
    const [seriesYears, setSeriesYears] = useState([])
    const [thresholdLines, setThresholdLines] = useState([])
    const [envelopeGroupLabel, setEnvelopeGroupLabel] = useState('')
    const [envelopeGroup, setEnvelopeGroup] = useState('auto')
    const [modelData, setModelData] = useState({ predMeasures: {}, selectedPred: undefined, predStats: [] })
    const [cumulPluvio, setCumulPluvio] = useState(undefined)
    const [chartOptions, setChartOptions] = useState({
        displayXGrid: true,
        displayYGrid: true,
        yScaleMode: 'auto',
        yMin: null,
        yMax: null,
        yInterval: null,
        modeDate: 'allDate',
    })
    const [displayEvents, setDisplayEvents] = useState(true)
    const [associationsSeries, setAssociationsSeries] = useState([])
    const [associationsAxis, setAssociationsAxis] = useState([])
    const [selectedAssociations, setSelectedAssociations] = useState([])
    const [paramsFrequencyStats, setParamsFrequencyStats] = useState({
        measures: [],
        showPreviousYears: [],
        histoYears: 0,
    })

    const dispatch = useDispatch()

    useEffect(() => {
        setStatsLoaded(false)
        dispatch(PluviometryAction.fetchPluviometerMeasuresStats(pluviometer.id)).then(() => setStatsLoaded(true))
        dispatch(ContributorAction.fetchContributors())
        dispatch(EventsAction.fetchStationEvents(STATION_TYPE_NAME.pluviometry, pluviometer.id))
        dispatch(StationAction.fetchAssociatedSites(pluviometer.code, getStationTypeCodeFromType(STATION_TYPE_NAME.pluviometry)))
        if (!pluviometryDataTypes.length) {
            dispatch(PluviometryAction.fetchPluviometryDataTypes())
        }
    }, [dispatch, pluviometer.id])

    useEffect(() => {
        setStat(pluviometerStatistics.find(p => p.typeId === 1)?.typeId ?? pluviometerStatistics[0]?.typeId)
    }, [pluviometerStatistics])

    const townName = useMemo(() => citiesIndex[pluviometer.townCode]?.name, [citiesIndex, pluviometer])

    useTitle(() => [{
        title: i18n.pluviometry,
        href: 'pluviometry',
    }, {
        title: getStationTitleWithTownName(pluviometer, townName),
        href: `station/pluviometry/${pluviometer.id}`,
    }, {
        title: i18n.pluviometricFollowUp,
        href: `station/pluviometry/${pluviometer.id}/pluviometricFollowUp`,
    }], [pluviometer.id, townName])

    useActions(() => ({
        links: getLinks(pluviometer),
        arrowNav: getStationArrowNav('pluviometry', pluviometers, pluviometer.id, s => dispatch(push(`/station/pluviometry/${s.id}/pluviometricFollowUp`))),
    }), [pluviometer.id])

    const tabs = [
        {
            icon: 'multiline_chart',
            constant: STATS_TAB,
            label: i18n.statistics,
        },
        ...(typeChart === GENERIC ? [{
            icon: 'fast_rewind',
            constant: PAST_YEARS,
            label: i18n.previousYears,
        }, {
            icon: 'list',
            constant: LINKEDSTATIONS_TAB,
            label: `${i18n.associatedStations} ${i18n.and} ${i18n.additionalData}`,
        }, {
            icon: 'aspect_ratio',
            constant: CHARTOPTIONS_TAB,
            label: i18n.chartOptions,
        }, {
            icon: 'report_problem',
            constant: THRESHOLDS_TAB,
            label: i18n.thresholds,
        }, {
            icon: 'event_note',
            constant: EVENTS_TAB,
            label: i18n.events,
        }, {
            icon: 'extension',
            constant: MODELS_TAB,
            label: i18n.prevData,
        }] : []),
    ]

    const currentUnit = pluviometerStatistics.find(s => s.typeId === stat)?.unit

    const onChangeDates = (dates) => {
        setMinDate(dates.minDate)
        setMaxDate(dates.maxDate)
        setTime(dates.tab)
    }

    return (
        <div style={{ overflow: 'hidden', position: 'relative' }} >

            <SimpleTabSideList
                position='right'
                defaultTab={STATS_TAB}
                tabs={tabs}
            >
                {tab => (
                    <>
                        <ChoiceTypeChart
                            typeChart={typeChart}
                            setTypeChart={setTypeChart}
                            pluviometerStatistics={pluviometerStatistics}
                            stat={stat}
                            setStat={setStat}
                            showEnveloppe={showEnveloppe}
                            dateComparedNormal={dateComparedNormal}
                            setDateComparedNormal={setDateComparedNormal}
                            setShowEnveloppe={setShowEnveloppe}
                            paramsFrequencyStats={paramsFrequencyStats}
                            setParamsFrequencyStats={setParamsFrequencyStats}
                            envelopeGroupLabel={envelopeGroupLabel}
                            envelopeGroup={envelopeGroup}
                            setEnvelopeGroup={setEnvelopeGroup}
                            tab={tab}
                        />
                        <PluvioSuiviPastYearsTab
                            changeParent={setSeriesYears}
                            time={time}
                            minDate={minDate}
                            maxDate={maxDate}
                            id={pluviometer.id}
                            dataType={stat}
                            cumulPluvio={cumulPluvio}
                            tab={tab}
                        />
                        <PluvioSuiviThresholdsTab
                            id={pluviometer.id}
                            changeParent={setThresholdLines}
                            time={time}
                            minDate={minDate}
                            maxDate={maxDate}
                            unit={currentUnit}
                            tab={tab}
                            typeId={stat}
                        />
                        <PluvioSuiviModelsTab
                            tab={tab}
                            pluviometerId={pluviometer.id}
                            typeId={stat}
                            cumulPluvio={cumulPluvio}
                            changeParent={setModelData}
                        />
                        <PluvioSuiviChartOptionsTab
                            tab={tab}
                            pluviometerId={pluviometer.id}
                            changeParent={setChartOptions}
                        />
                        {tab === EVENTS_TAB && (
                            <StationSuiviEventsTab
                                id={pluviometer.id}
                                stationType={STATION_TYPE_NAME.pluviometry}
                                displayEvents={displayEvents}
                                changeParent={({ displayEvents: v }) => setDisplayEvents(v)}
                            />
                        )}
                        <PluvioSuiviAssociationsTab
                            tab={tab}
                            pluviometer={pluviometer}
                            typeId={stat}
                            time={time}
                            minDate={minDate}
                            maxDate={maxDate}
                            changeParent={({ associationsSeries: s, associationsAxis: a, selectedAssociations: sa }) => {
                                setAssociationsSeries(s)
                                setAssociationsAxis(a)
                                setSelectedAssociations(sa)
                            }}
                        />
                    </>
                )}
            </SimpleTabSideList>
            <Grid container spacing={2} margin={1}>
                <Grid size={12} minHeight={window.innerHeight}>

                    {!statsLoaded && (
                        <ProgressCard indeterminate />
                    )}
                    {statsLoaded && !pluviometerStatistics.length && (
                        <MessageCard>{i18n.noDataTypeDefined}</MessageCard>
                    )}
                    {statsLoaded && !!pluviometerStatistics.length && (
                        <Card>
                            <CardContent>
                                {!!pluviometerStatistics.length && (
                                    <>
                                        {typeChart === GENERIC && (
                                            <GraphGenericPluvio
                                                pluviometer={pluviometer}
                                                stat={stat}
                                                showEnveloppe={showEnveloppe}
                                                pluviometerStatistics={pluviometerStatistics}
                                                paramsFrequencyStats={paramsFrequencyStats}
                                                setParamsFrequencyStats={setParamsFrequencyStats}
                                                seriesYears={seriesYears}
                                                thresholdLines={thresholdLines}
                                                envelopeGroup={envelopeGroup}
                                                modelData={modelData}
                                                chartOptions={chartOptions}
                                                displayEvents={displayEvents}
                                                associationsSeries={associationsSeries}
                                                associationsAxis={associationsAxis}
                                                onChangeDatesParent={onChangeDates}
                                                onEnvelopeGroupChange={setEnvelopeGroupLabel}
                                                onCumulPluvioChange={setCumulPluvio}
                                            />
                                        )}
                                        {typeChart === NORMALS_YEARS && (
                                            <NormalsGraphPluvio pluviometer={pluviometer} typeGroupFunc='YEARS' />
                                        )}
                                        {typeChart === NORMALS_MONTHS && (
                                            <NormalsGraphPluvio pluviometer={pluviometer} />
                                        )}
                                        {typeChart === MONTHS_STACK && (
                                            <GraphMonthsStackRain pluviometer={pluviometer} />
                                        )}
                                        {typeChart === EFFICIENT_RAIN && (
                                            <GraphEfficientRain pluviometer={pluviometer} pluviometerStatistics={pluviometerStatistics} />
                                        )}
                                        {typeChart === NORMAL_COMPARISON && (
                                            <GraphComparisonNormal
                                                pluviometer={pluviometer}
                                                stat={stat}
                                                dateComparedNormal={dateComparedNormal}
                                                pluviometerStatistics={pluviometerStatistics}
                                            />
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </div>
    )
}

export default PluvioSuiviGraphApp
